import React, { FormEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { AppsSDKUIProvider } from "@openai/apps-sdk-ui/components/AppsSDKUIProvider";
import JsonView from "@uiw/react-json-view";
import { githubDarkTheme } from "@uiw/react-json-view/githubDark";
import { githubLightTheme } from "@uiw/react-json-view/githubLight";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ImportProfileDialog, ProfileDialog, ProfileMenu, readTravellerProfile } from "./demo_profile.tsx";
import { ItineraryWidget } from "./itineraries.tsx";
import { buildResultsState, integrateWidget, itineraryCount, type ResultsState } from "./result_sets.ts";
import { appPath } from "./app_path.ts";

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
  widgets?: any[];
  tool_calls?: ToolCall[];
  usage?: TokenUsage;
};

type ToolCall = {
  name: string;
  arguments: any;
  response: any;
};

type TokenUsage = {
  model?: string;
  reasoning_effort?: string;
  input_tokens: number;
  cached_input_tokens: number;
  cache_write_tokens: number;
  output_tokens: number;
  total_tokens: number;
  estimated_cost_usd: number;
};

type DemoState = {
  messages?: ChatMessage[];
  widgets?: any[];
};

type OpenAIModelSelection = {
  provider: "chatgpt";
  version: "5.6" | "5.5";
  model: "sol" | "terra" | "luna";
  thinking: "instant" | "none" | "low" | "medium" | "high" | "xhigh" | "max";
};

type AnthropicModelSelection = {
  provider: "claude";
  version: "5" | "4.8";
  model: "mythos" | "fable" | "opus" | "sonnet";
  thinking: "low" | "medium" | "high" | "xhigh" | "max";
};

type GeminiModelSelection = {
  provider: "gemini";
  version: "3.1" | "3.5" | "3.6" | "3.7" | "3.8";
  model: "pro" | "flash" | "flash-lite";
  thinking: "minimal" | "low" | "medium" | "high";
};

type ModelSelection = OpenAIModelSelection | AnthropicModelSelection | GeminiModelSelection;

const DEFAULT_OPENAI_SELECTION: OpenAIModelSelection = { provider: "chatgpt", version: "5.6", model: "sol", thinking: "medium" };
const DEFAULT_ANTHROPIC_SELECTION: AnthropicModelSelection = { provider: "claude", version: "5", model: "sonnet", thinking: "high" };
const DEFAULT_GEMINI_SELECTION: GeminiModelSelection = { provider: "gemini", version: "3.8", model: "flash", thinking: "medium" };
const MODEL_SELECTION_COOKIE_NAME = "reagent_demo_model_selection";
const MODEL_SELECTION_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

function isStoredModelSelection(value: unknown): value is ModelSelection {
  if (!value || typeof value !== "object") return false;

  const selection = value as Record<string, unknown>;
  if (selection.provider === "chatgpt") {
    const validThinking = selection.version === "5.5"
      ? ["instant", "none", "low", "medium", "high", "xhigh"].includes(String(selection.thinking))
      : ["instant", "none", "low", "medium", "high", "xhigh", "max"].includes(String(selection.thinking));
    return ["5.6", "5.5"].includes(String(selection.version)) &&
      ["sol", "terra", "luna"].includes(String(selection.model)) &&
      validThinking;
  }
  if (selection.provider === "claude") {
    const validModel = selection.version === "4.8"
      ? selection.model === "opus"
      : selection.version === "5" && ["mythos", "fable", "opus", "sonnet"].includes(String(selection.model));
    return validModel && ["low", "medium", "high", "xhigh", "max"].includes(String(selection.thinking));
  }
  if (selection.provider === "gemini") {
    const validModel = (selection.version === "3.1" && ["pro", "flash-lite"].includes(String(selection.model))) ||
      (selection.version === "3.5" && ["flash", "flash-lite"].includes(String(selection.model))) ||
      (["3.6", "3.7", "3.8"].includes(String(selection.version)) && selection.model === "flash");
    const validThinking = ["minimal", "low", "medium", "high"].includes(String(selection.thinking)) &&
      !(selection.thinking === "minimal" && ((selection.version === "3.1" && selection.model === "pro") || ["3.7", "3.8"].includes(String(selection.version))));
    return validModel && validThinking;
  }

  return false;
}

function readStoredModelSelection(): ModelSelection | undefined {
  const rawCookie = document.cookie
    .split("; ")
    .find((cookie) => cookie.startsWith(`${MODEL_SELECTION_COOKIE_NAME}=`))
    ?.slice(MODEL_SELECTION_COOKIE_NAME.length + 1);
  if (!rawCookie) return;

  try {
    const parsed = JSON.parse(decodeURIComponent(rawCookie));
    return parsed?.v === 1 && isStoredModelSelection(parsed.selection) ? parsed.selection : undefined;
  } catch {
    return;
  }
}

function storeModelSelection(selection: ModelSelection) {
  const value = encodeURIComponent(JSON.stringify({ v: 1, selection }));
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${MODEL_SELECTION_COOKIE_NAME}=${value}; Path=/; Max-Age=${MODEL_SELECTION_COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

type StreamEvent = {
  event: string;
  data: any;
};

type RecoveredRequest = {
  input: string;
  action?: "submit" | "reset";
  notice?: string;
  reloadAttempts?: number;
};

async function isStaleClientResponse(response: Response) {
  if (response.status !== 409) return false;

  try {
    return (await response.json())?.error?.code === "STALE_CLIENT";
  } catch {
    return false;
  }
}

function recoveryStorageKey() {
  return `demo-recovery:${window.location.pathname}`;
}

function parseRecoveredRequest(request: any): RecoveredRequest | undefined {
  if (typeof request?.input !== "string") return;
  if (request.action && request.action !== "submit" && request.action !== "reset") return;
  if (request.notice && typeof request.notice !== "string") return;
  if (request.reloadAttempts && (!Number.isInteger(request.reloadAttempts) || request.reloadAttempts < 0)) return;
  return request;
}

function takeRecoveredRequest(): RecoveredRequest | undefined {
  const key = recoveryStorageKey();

  try {
    const rawRequest = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
    if (rawRequest) return parseRecoveredRequest(JSON.parse(rawRequest));
  } catch {
    // Fall back to history state when session storage is unavailable.
  }

  try {
    const state = window.history.state;
    const request = parseRecoveredRequest(state?.demoRecovery);
    if (state?.demoRecovery) {
      const { demoRecovery: _, ...rest } = state;
      window.history.replaceState(rest, "", window.location.href);
    }
    return request;
  } catch {
    return;
  }
}

const STARTER_PROMPTS = [
  {
    label: "Get me from SFO to New York next Monday, not arriving too late. I need to head back late morning Wednesday.",
    prompt: "Get me from SFO to New York next Monday, not arriving too late. I need to head back late morning Wednesday",
  },
  {
    label: "LAX to Tokyo next Tuesday, back Friday. Nonstop business class with Wi-Fi and a fully flat seat.",
    prompt: "LAX to Tokyo next Tuesday, back Friday. Nonstop business class with Wi-Fi and a fully flat seat.",
  },
  {
    label: "I need to go back to Chicago this Thursday, same as last week.",
    prompt: "Minneapolis to O'Hare Thursday for 1 night. Arriving before 9 on the way out and leaving after 6:15 on the way back. UA preferred in first class. Last time I flew UA307 out and UA2788 back. No turboprops.",
  },
  {
    label: "London to Chicago on the 14th to 21st. Direct BA operated only.",
    prompt: "London to Chicago on the 14th to 21st. Direct BA operated only.",
  },
];

function shouldUseCompactStarterPrompts() {
  const widths = [
    window.innerWidth,
    window.visualViewport?.width,
    document.documentElement.clientWidth,
    window.screen?.width,
  ].filter((width): width is number => typeof width === "number" && Number.isFinite(width));
  const narrowViewport = widths.length > 0 && Math.min(...widths) < 768;
  const narrowTouchScreen = (navigator.maxTouchPoints || 0) > 0 && (window.screen?.width || 0) < 932;
  const mobileUserAgent = /Android|iPhone|iPod|Mobile/i.test(navigator.userAgent);

  return narrowViewport || narrowTouchScreen || mobileUserAgent;
}

function currentChatId() {
  const match = window.location.pathname.match(new RegExp(`^${appPath("/demo")}/([^/?#]+)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function parseSSEEvent(rawEvent: string): StreamEvent | null {
  const lines = rawEvent.split("\n");
  const event = lines.find((line) => line.startsWith("event:"))?.replace(/^event:\s*/, "") || "message";
  const data = lines
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.replace(/^data:\s*/, ""))
    .join("\n");

  if (!data) return null;

  try {
    return { event, data: JSON.parse(data) };
  } catch {
    return { event, data };
  }
}

function appendAssistantDelta(messages: ChatMessage[], delta: string): ChatMessage[] {
  const next = [...messages];
  const last = next[next.length - 1];

  if (last?.role === "assistant") {
    next[next.length - 1] = { ...last, content: last.content + delta };
  } else {
    next.push({ role: "assistant", content: delta });
  }

  return next;
}

function appendAssistantWidget(messages: ChatMessage[], widget: any): ChatMessage[] {
  const next = [...messages];
  const last = next[next.length - 1];

  if (last?.role === "assistant") {
    next[next.length - 1] = { ...last, widgets: [...(last.widgets || []), widget] };
  } else {
    next.push({ role: "assistant", content: "", widgets: [widget] });
  }

  return next;
}

function appendAssistantToolCall(messages: ChatMessage[], toolCall: ToolCall): ChatMessage[] {
  const next = [...messages];
  const last = next[next.length - 1];

  if (last?.role === "assistant") {
    next[next.length - 1] = { ...last, tool_calls: [...(last.tool_calls || []), toolCall] };
  } else {
    next.push({ role: "assistant", content: "", tool_calls: [toolCall] });
  }

  return next;
}

function restoreResults(messages: ChatMessage[], widgets: any[] = []) {
  const messageWidgets = messages.flatMap((message) => message.widgets || []);
  return buildResultsState([...messageWidgets, ...widgets]);
}

function suggestionTexts(markdown: string) {
  return Array.from(markdown.matchAll(/\[([^\]]+)\]\(#suggestion\)/g), (match) => match[1]);
}

function MarkdownMessage({ text, onSuggestion, suggestionsVisible }: { text: string; onSuggestion: (suggestion: string) => void; suggestionsVisible: boolean }) {
  return (
    <ReactMarkdown
      remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
      skipHtml
      components={{
        p: ({ node, ...props }) => <p className="mb-3 last:mb-0" {...props} />,
        h1: ({ node, ...props }) => <h1 className="mb-3 mt-5 first:mt-0 text-xl font-semibold tracking-tight last:mb-0" {...props} />,
        h2: ({ node, ...props }) => <h2 className="mb-3 mt-5 first:mt-0 text-lg font-semibold tracking-tight last:mb-0" {...props} />,
        h3: ({ node, ...props }) => <h3 className="mb-2 mt-4 first:mt-0 font-semibold tracking-tight last:mb-0" {...props} />,
        h4: ({ node, ...props }) => <h4 className="mb-2 mt-4 first:mt-0 font-semibold tracking-tight last:mb-0" {...props} />,
        h5: ({ node, ...props }) => <h5 className="mb-2 mt-4 first:mt-0 text-sm font-semibold uppercase tracking-wide last:mb-0" {...props} />,
        h6: ({ node, ...props }) => <h6 className="mb-2 mt-4 first:mt-0 text-sm font-semibold uppercase tracking-wide text-gray-600 last:mb-0 dark:text-gray-300" {...props} />,
        ul: ({ node, ...props }) => <ul className="mb-3 list-disc space-y-1 ps-5 last:mb-0 [&:has([data-suggestion])]:list-none [&:has([data-suggestion])]:space-y-2 [&:has([data-suggestion])]:ps-0 [&:has([data-suggestion][hidden])]:hidden" {...props} />,
        ol: ({ node, ...props }) => <ol className="mb-3 list-decimal space-y-1 ps-5 last:mb-0" {...props} />,
        li: ({ node, ...props }) => <li className="ps-1 [&:has([data-suggestion])]:ps-0" {...props} />,
        a: ({ node, href, children, ...props }) => href === "#suggestion" ? (
          <button
            type="button"
            data-suggestion=""
            hidden={!suggestionsVisible}
            onClick={(event) => onSuggestion(event.currentTarget.textContent || "")}
            className="group flex w-full items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50/50 px-4 py-3 text-left text-[15px] font-medium leading-6 text-blue-950 transition-colors hover:border-blue-300 hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:border-blue-400/30 dark:bg-blue-400/10 dark:text-blue-100 dark:hover:border-blue-400/50 dark:hover:bg-blue-400/15 dark:focus-visible:ring-blue-400 dark:focus-visible:ring-offset-black"
          >
            <span>{children}</span>
            <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className="h-4 w-4 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5 dark:text-gray-500">
              <path d="M4 10h11m-4-4 4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ) : (
          <a className="font-medium text-blue-700 underline underline-offset-2 dark:text-blue-300" target="_blank" rel="noreferrer" href={href} {...props}>{children}</a>
        ),
        blockquote: ({ node, ...props }) => <blockquote className="mb-3 border-l-2 border-gray-300 ps-4 text-gray-700 last:mb-0 dark:border-gray-600 dark:text-gray-200" {...props} />,
        code: ({ node, className, ...props }) => <code className={`rounded bg-gray-100 px-1 py-0.5 text-[0.92em] dark:bg-white/10 ${className || ""}`} {...props} />,
        pre: ({ node, ...props }) => <pre className="mb-3 overflow-x-auto rounded-2xl bg-gray-950 p-4 text-sm leading-6 text-gray-100 last:mb-0 [&_code]:bg-transparent [&_code]:p-0" {...props} />,
        hr: ({ node, ...props }) => <hr className="my-5 border-gray-200 dark:border-gray-700" {...props} />,
        table: ({ node, ...props }) => <div className="mb-3 overflow-x-auto last:mb-0"><table className="min-w-full border-collapse text-left text-sm" {...props} /></div>,
        th: ({ node, ...props }) => <th className="border-b border-gray-300 px-3 py-2 font-semibold dark:border-gray-600" {...props} />,
        td: ({ node, ...props }) => <td className="border-b border-gray-200 px-3 py-2 dark:border-gray-700" {...props} />,
        input: ({ node, ...props }) => <input className="me-2 align-middle" readOnly {...props} />,
      }}
    >
      {text}
    </ReactMarkdown>
  );
}

function ResultsWidget({ widget }: { widget: any }) {
  const widgetTheme = document.documentElement.classList.contains("dark") ||
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";

  return (
    <div className="demo-itinerary-widget w-full bg-white py-2 dark:bg-black" data-demo-theme={widgetTheme}>
      <AppsSDKUIProvider>
        <ItineraryWidget toolOutputOverride={widget} themeOverride={widgetTheme} />
      </AppsSDKUIProvider>
    </div>
  );
}

function formatTokenCount(value?: number) {
  return Number(value || 0).toLocaleString();
}

function formatEstimatedCost(value?: number) {
  const cost = Number(value || 0);
  return `$${cost.toFixed(2)}`;
}

function addUsage(a: TokenUsage | undefined, b: TokenUsage | undefined): TokenUsage | undefined {
  if (!a && !b) return undefined;

  return {
    model: b?.model || a?.model,
    reasoning_effort: b?.reasoning_effort || a?.reasoning_effort,
    input_tokens: (a?.input_tokens || 0) + (b?.input_tokens || 0),
    cached_input_tokens: (a?.cached_input_tokens || 0) + (b?.cached_input_tokens || 0),
    cache_write_tokens: (a?.cache_write_tokens || 0) + (b?.cache_write_tokens || 0),
    output_tokens: (a?.output_tokens || 0) + (b?.output_tokens || 0),
    total_tokens: (a?.total_tokens || 0) + (b?.total_tokens || 0),
    estimated_cost_usd: (a?.estimated_cost_usd || 0) + (b?.estimated_cost_usd || 0),
  };
}

function cumulativeUsage(messages: ChatMessage[], messageIndex: number) {
  return messages.slice(0, messageIndex + 1).reduce<TokenUsage | undefined>((total, message) => addUsage(total, message.usage), undefined);
}

function UsageMetric({ label, value, wide = false }: { label: string; value: string; wide?: boolean }) {
  return (
    <div className={wide ? "col-span-2 min-w-0" : "min-w-0"}>
      <div className="text-[9px] font-medium uppercase leading-3 tracking-wide text-gray-350 dark:text-gray-500">{label}</div>
      <div className={`${wide ? "whitespace-nowrap" : "truncate"} font-mono text-[11px] leading-4 text-gray-700 dark:text-gray-200 xl:text-[11px]`}>{value}</div>
    </div>
  );
}

function UsageSummary({ usage, conversationUsage }: { usage: TokenUsage; conversationUsage?: TokenUsage }) {
  return (
    <div className="space-y-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-gray-500 dark:border-white/10 dark:bg-black/30 dark:text-gray-400">
      <div className="grid grid-cols-2 gap-x-4 gap-y-2">
        <UsageMetric label="provider/version/model" value={usage.model || "unknown"} wide />
        <UsageMetric label="effort" value={usage.reasoning_effort || "unknown"} />
        <UsageMetric label="turn cost" value={formatEstimatedCost(usage.estimated_cost_usd)} />
        <UsageMetric label="turn tokens" value={formatTokenCount(usage.total_tokens)} />
        <UsageMetric label="input" value={formatTokenCount(usage.input_tokens)} />
        <UsageMetric label="cached" value={formatTokenCount(usage.cached_input_tokens)} />
        <UsageMetric label="cache write" value={formatTokenCount(usage.cache_write_tokens)} />
        <UsageMetric label="output" value={formatTokenCount(usage.output_tokens)} />
      </div>
      {conversationUsage ? (
        <div className="grid grid-cols-2 gap-x-4 border-t border-gray-100 pt-2 dark:border-white/10">
          <UsageMetric label="conversation cost" value={formatEstimatedCost(conversationUsage.estimated_cost_usd)} />
          <UsageMetric label="conversation tokens" value={formatTokenCount(conversationUsage.total_tokens)} />
        </div>
      ) : null}
    </div>
  );
}

function ToolCallDetails({ toolCall }: { toolCall: ToolCall }) {
  return (
    <details className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs leading-5 text-gray-700 dark:border-white/10 dark:bg-black/30 dark:text-gray-200">
      <summary className="cursor-pointer select-none font-semibold text-gray-800 marker:text-gray-500 dark:text-gray-100 dark:marker:text-gray-300">
        Tool call: <span className="font-mono">{toolCall.name}</span>
      </summary>
      <div className="mt-3 grid gap-3">
        <div className="min-w-0">
          <div className="mb-1 font-medium uppercase tracking-wide text-gray-500 dark:text-gray-300">Arguments</div>
          <JsonTree value={toolCall.arguments} defaultOpen />
        </div>
        <div className="min-w-0">
          <div className="mb-1 font-medium uppercase tracking-wide text-gray-500 dark:text-gray-300">Response</div>
          <JsonTree value={toolCall.response} defaultOpen />
        </div>
      </div>
    </details>
  );
}

function ToolCallsDebug({ toolCalls, usage, conversationUsage, suggestions }: { toolCalls: ToolCall[]; usage?: TokenUsage; conversationUsage?: TokenUsage; suggestions: string[] }) {
  return (
    <details className="mx-3 mt-1 text-right text-[10px] leading-4 sm:mx-0">
      <summary className="ml-auto inline-flex cursor-pointer select-none list-none items-center rounded-full border border-gray-50 bg-white/30 px-1 py-0 text-[10px] font-medium text-gray-300/70 transition hover:border-gray-200 hover:bg-gray-50/60 hover:text-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-100 dark:border-white/[0.06] dark:bg-white/[0.02] dark:text-gray-600 dark:hover:border-white/20 dark:hover:bg-white/[0.04] dark:hover:text-gray-400 dark:focus:ring-white/20 [&::-webkit-details-marker]:hidden">
        debug
      </summary>
      <div className="mt-2 space-y-2 rounded-2xl border border-gray-200 bg-gray-50 p-2 text-left dark:border-white/10 dark:bg-white/[0.04]">
        {usage ? <UsageSummary usage={usage} conversationUsage={conversationUsage} /> : null}
        {toolCalls.map((toolCall, toolCallIndex) => (
          <ToolCallDetails key={`${toolCall.name}-${toolCallIndex}`} toolCall={toolCall} />
        ))}
        {suggestions.length ? (
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs leading-5 text-gray-700 dark:border-white/10 dark:bg-black/30 dark:text-gray-200">
            <div className="font-medium uppercase tracking-wide text-gray-500 dark:text-gray-300">Returned options</div>
            <ul className="mt-1 list-disc space-y-0.5 ps-4">
              {suggestions.map((suggestion, index) => <li key={`${suggestion}-${index}`}>{suggestion}</li>)}
            </ul>
          </div>
        ) : null}
      </div>
    </details>
  );
}

function JsonTree({ value, defaultOpen = false }: { value: any; defaultOpen?: boolean }) {
  return (
    <div className="max-h-72 overflow-auto rounded-lg border border-gray-200 bg-white p-3 text-left dark:border-white/10 dark:bg-[#0d1117]">
      <JsonView
        value={value}
        collapsed={defaultOpen ? 3 : 1}
        displayDataTypes={false}
        displayObjectSize={false}
        enableClipboard
        shortenTextAfterLength={80}
        indentWidth={16}
        style={{
          ...(document.documentElement.classList.contains("dark") || window.matchMedia?.("(prefers-color-scheme: dark)").matches ? githubDarkTheme : githubLightTheme),
          backgroundColor: "transparent",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          fontSize: "11px",
          lineHeight: "1.55",
        }}
      />
    </div>
  );
}

function ModelSelector({ selection, onChange, detailsRef }: {
  selection: ModelSelection;
  onChange: (selection: ModelSelection) => void;
  detailsRef: React.RefObject<HTMLDetailsElement | null>;
}) {
  const titleCase = (value: string) => `${value[0].toUpperCase()}${value.slice(1)}`;
  const modelLabel = (value: string) => value === "flash-lite" ? "Flash Lite" : titleCase(value);
  const effectiveLabel = selection.provider === "claude"
    ? `Claude ${selection.version} ${titleCase(selection.model)} ${titleCase(selection.thinking)}`
    : selection.provider === "gemini"
      ? `Gemini ${selection.version} ${modelLabel(selection.model)} ${titleCase(selection.thinking)}`
    : selection.version === "5.5"
        ? `ChatGPT 5.5 ${titleCase(selection.thinking)}`
        : `ChatGPT 5.6 ${titleCase(selection.model)} ${titleCase(selection.thinking)}`;
  const usesOpenAIVariant = selection.provider === "chatgpt" && selection.version === "5.6";

  return (
    <details ref={detailsRef} className="group relative shrink-0">
      <summary className="flex h-9 cursor-pointer list-none items-center gap-1.5 rounded-full bg-gray-100 px-3 text-xs font-medium text-gray-700 transition hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 dark:bg-white/10 dark:text-gray-200 dark:hover:bg-white/15 dark:focus-visible:ring-gray-500 [&::-webkit-details-marker]:hidden">
        <span className="max-w-32 truncate sm:max-w-none">{effectiveLabel}</span>
        <span aria-hidden="true" className="text-[9px] text-gray-500 transition group-open:rotate-180 dark:text-gray-400">▲</span>
      </summary>
      <div className="absolute -right-12 bottom-full z-20 mb-2 w-[min(30rem,calc(100vw-2rem))] rounded-2xl border border-gray-200 bg-white p-3 text-left shadow-xl dark:border-white/10 dark:bg-[#252525]">
        <div className="mb-3">
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Model</div>
          <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Locked for this conversation after you send.</div>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <label className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Provider
            <select
              aria-label="Model provider"
              value={selection.provider}
              onChange={(event) => {
                if (event.target.value === "claude") onChange(DEFAULT_ANTHROPIC_SELECTION);
                else if (event.target.value === "gemini") onChange(DEFAULT_GEMINI_SELECTION);
                else onChange(DEFAULT_OPENAI_SELECTION);
              }}
              className="mt-1 h-10 w-full rounded-xl border border-gray-200 bg-white px-2 text-sm normal-case text-gray-900 outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-200 dark:border-white/15 dark:bg-[#303030] dark:text-gray-100 dark:focus:border-white/30 dark:focus:ring-white/10"
            >
              <option value="chatgpt">ChatGPT</option>
              <option value="claude">Claude</option>
              <option value="gemini">Gemini</option>
            </select>
          </label>
          <label className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Version
            <select
              aria-label="Model version"
              value={selection.version}
              onChange={(event) => {
                if (selection.provider === "claude") {
                  const version = event.target.value as AnthropicModelSelection["version"];
                  onChange({ ...selection, version, model: version === "4.8" ? "opus" : selection.model });
                } else if (selection.provider === "gemini") {
                  const version = event.target.value as GeminiModelSelection["version"];
                  onChange({
                    ...selection,
                    version,
                    model: version === "3.1" ? "pro" : "flash",
                    thinking: (version === "3.1" || ["3.7", "3.8"].includes(version)) && selection.thinking === "minimal" ? "medium" : selection.thinking,
                  });
                } else {
                  const version = event.target.value as OpenAIModelSelection["version"];
                  onChange({ ...selection, version, thinking: version === "5.5" && selection.thinking === "max" ? "medium" : selection.thinking });
                }
              }}
              className="mt-1 h-10 w-full rounded-xl border border-gray-200 bg-white px-2 text-sm normal-case text-gray-900 outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-200 dark:border-white/15 dark:bg-[#303030] dark:text-gray-100 dark:focus:border-white/30 dark:focus:ring-white/10"
            >
              {selection.provider === "claude" ? (
                <>
                  <option value="5">5</option>
                  <option value="4.8">4.8</option>
                </>
              ) : selection.provider === "gemini" ? (
                <>
                  <option value="3.1">3.1</option>
                  <option value="3.5">3.5</option>
                  <option value="3.6">3.6</option>
                  <option value="3.7">3.7</option>
                  <option value="3.8">3.8</option>
                </>
              ) : (
                <>
                  <option value="5.6">5.6</option>
                  <option value="5.5">5.5</option>
                </>
              )}
            </select>
          </label>
          <label className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Model
            <select
              aria-label="Model variant"
              value={selection.model}
              disabled={selection.provider === "gemini" ? ["3.6", "3.7", "3.8"].includes(selection.version) : (selection.provider === "claude" ? selection.version === "4.8" : !usesOpenAIVariant)}
              onChange={(event) => {
                if (selection.provider === "claude") {
                  onChange({ ...selection, model: event.target.value as AnthropicModelSelection["model"] });
                } else if (selection.provider === "gemini") {
                  const model = event.target.value as GeminiModelSelection["model"];
                  onChange({ ...selection, model, thinking: model === "flash-lite" ? "medium" : selection.thinking === "minimal" ? "medium" : selection.thinking });
                } else if (selection.provider === "chatgpt") {
                  onChange({ ...selection, model: event.target.value as OpenAIModelSelection["model"] });
                }
              }}
              className="mt-1 h-10 w-full rounded-xl border border-gray-200 bg-white px-2 text-sm normal-case text-gray-900 outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-200 disabled:cursor-not-allowed disabled:opacity-45 dark:border-white/15 dark:bg-[#303030] dark:text-gray-100 dark:focus:border-white/30 dark:focus:ring-white/10"
            >
              {selection.provider === "claude" ? (
                <>
                  {selection.version === "5" ? <option value="mythos">Mythos</option> : null}
                  {selection.version === "5" ? <option value="fable">Fable</option> : null}
                  <option value="opus">Opus</option>
                  {selection.version === "5" ? <option value="sonnet">Sonnet</option> : null}
                </>
              ) : selection.provider === "gemini" ? (
                <>
                  {selection.version === "3.1" ? <option value="pro">Pro</option> : <option value="flash">Flash</option>}
                  {!["3.6", "3.7", "3.8"].includes(selection.version) ? <option value="flash-lite">Flash Lite</option> : null}
                </>
              ) : (
                <>
                  <option value="sol">Sol</option>
                  <option value="terra">Terra</option>
                  <option value="luna">Luna</option>
                </>
              )}
            </select>
          </label>
          <label className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Thinking
            <select
              aria-label="Thinking level"
              value={selection.thinking}
              onChange={(event) => {
                if (selection.provider === "claude") {
                  onChange({ ...selection, thinking: event.target.value as AnthropicModelSelection["thinking"] });
                } else if (selection.provider === "gemini") {
                  onChange({ ...selection, thinking: event.target.value as GeminiModelSelection["thinking"] });
                } else {
                  onChange({ ...selection, thinking: event.target.value as OpenAIModelSelection["thinking"] });
                }
              }}
              className="mt-1 h-10 w-full rounded-xl border border-gray-200 bg-white px-2 text-sm normal-case text-gray-900 outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-200 dark:border-white/15 dark:bg-[#303030] dark:text-gray-100 dark:focus:border-white/30 dark:focus:ring-white/10"
            >
              {selection.provider === "claude" ? (
                <>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="xhigh">XHigh</option>
                  <option value="max">Max</option>
                </>
              ) : selection.provider === "gemini" ? (
                <>
                  {(selection.version !== "3.1" || selection.model === "flash-lite") && !["3.7", "3.8"].includes(selection.version) ? <option value="minimal">Minimal</option> : null}
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </>
              ) : (
                <>
                  <option value="instant">Instant</option>
                  <option value="none">None</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="xhigh">XHigh</option>
                  {selection.version === "5.6" ? <option value="max">Max</option> : null}
                </>
              )}
            </select>
          </label>
        </div>
        {selection.provider === "chatgpt" && selection.thinking === "instant" ? (
          <p className="mt-2 text-xs leading-4 text-gray-500 dark:text-gray-400">Instant uses the selected model with reasoning disabled.</p>
        ) : null}
      </div>
    </details>
  );
}

function DemoApp({
  demoRelease,
  debugEnabled = true,
  hideModelSelector = false,
  recoveredRequest,
}: {
  demoRelease?: string;
  debugEnabled?: boolean;
  hideModelSelector?: boolean;
  recoveredRequest?: RecoveredRequest;
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [resultsState, setResultsState] = useState<ResultsState>({ sets: [], selectedId: null });
  const [resultsOpen, setResultsOpen] = useState(false);
  const [resultsUpdated, setResultsUpdated] = useState(false);
  const [responseReadingSpace, setResponseReadingSpace] = useState(0);
  const [input, setInput] = useState(recoveredRequest?.input || "");
  const [loading, setLoading] = useState(false);
  const [processingMessage, setProcessingMessage] = useState("Thinking...");
  const [initialStateLoaded, setInitialStateLoaded] = useState(false);
  const [error, setError] = useState<string | null>(recoveredRequest?.notice || null);
  const [isDarkMode, setIsDarkMode] = useState(() => document.documentElement.classList.contains("dark") || window.matchMedia?.("(prefers-color-scheme: dark)").matches);
  const [useCompactStarterPrompts, setUseCompactStarterPrompts] = useState(shouldUseCompactStarterPrompts);
  const [modelSelection, setModelSelection] = useState<ModelSelection>(() => (
    hideModelSelector ? DEFAULT_GEMINI_SELECTION : readStoredModelSelection() || DEFAULT_GEMINI_SELECTION
  ));
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileImportOpen, setProfileImportOpen] = useState(false);
  const [profileRevision, setProfileRevision] = useState(0);
  const [recoveryPending, setRecoveryPending] = useState(!!recoveredRequest?.action);
  const chatScrollRef = useRef<HTMLDivElement | null>(null);
  const messageInputRef = useRef<HTMLTextAreaElement | null>(null);
  const resultsLauncherRef = useRef<HTMLButtonElement | null>(null);
  const resultsOpenRef = useRef(false);
  const shouldAutoScrollRef = useRef(true);
  const suppressResponseAutoScrollRef = useRef(false);
  const latestUserMessageRef = useRef<HTMLDivElement | null>(null);
  const pinSubmittedMessageRef = useRef(false);
  const modelSelectorRef = useRef<HTMLDetailsElement | null>(null);
  const demoMenuButtonRef = useRef<HTMLElement | null>(null);
  const scrollFrameRef = useRef<number | null>(null);
  const recoveredActionRef = useRef(recoveredRequest?.action);
  const recoveryAttemptsRef = useRef(recoveredRequest?.reloadAttempts || 0);

  const releaseHeaders = demoRelease ? { "X-Demo-Release": demoRelease } : {};

  function resizeMessageInput(textarea = messageInputRef.current) {
    if (!textarea) return;

    textarea.style.height = "auto";
    textarea.style.overflowY = "hidden";
    const styles = window.getComputedStyle(textarea);
    const fontSize = Number.parseFloat(styles.fontSize);
    const lineHeight = Number.parseFloat(styles.lineHeight) || fontSize * 1.2;
    const padding = Number.parseFloat(styles.paddingTop) + Number.parseFloat(styles.paddingBottom);
    const maxHeight = (lineHeight * 4) + padding;
    const nextHeight = Math.min(textarea.scrollHeight, maxHeight);

    textarea.style.height = `${nextHeight}px`;
    textarea.style.maxHeight = `${maxHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? "auto" : "hidden";
  }

  function selectModel(selection: ModelSelection) {
    setModelSelection(selection);
    storeModelSelection(selection);
  }

  function reloadWithRecoveredInput(
    recoveredInput: string,
    { action, notice, reloadAttempts = 0 }: Omit<RecoveredRequest, "input"> = {},
  ) {
    if (reloadAttempts >= 2) {
      recoveredActionRef.current = undefined;
      recoveryAttemptsRef.current = 0;
      setRecoveryPending(false);
      setInput(recoveredInput);
      setError("The connection could not be refreshed automatically. Please refresh the page and try again.");
      return;
    }

    const request = { input: recoveredInput, action, notice, reloadAttempts: reloadAttempts + 1 };
    try {
      sessionStorage.setItem(recoveryStorageKey(), JSON.stringify(request));
      window.location.reload();
    } catch {
      try {
        window.history.replaceState({ ...window.history.state, demoRecovery: request }, "", window.location.href);
        window.location.reload();
      } catch {
        recoveredActionRef.current = undefined;
        setRecoveryPending(false);
        setInput(recoveredInput);
        setError("The connection could not be refreshed automatically. Please refresh the page and try again.");
      }
    }
  }

  function isNearBottom() {
    const container = chatScrollRef.current;
    if (!container) return true;
    return container.scrollHeight - container.scrollTop - container.clientHeight < 160;
  }

  function scrollToLatestMessage(force = false) {
    if (!force && suppressResponseAutoScrollRef.current) return;
    if (!force && !shouldAutoScrollRef.current) return;
    if (force) shouldAutoScrollRef.current = true;
    if (scrollFrameRef.current !== null) return;

    scrollFrameRef.current = window.requestAnimationFrame(() => {
      scrollFrameRef.current = null;
      const container = chatScrollRef.current;
      if (container) container.scrollTop = container.scrollHeight;
    });
  }

  function toggleResultSet(resultSetId: string) {
    const shouldClose = resultsOpenRef.current && resultsState.selectedId === resultSetId;
    resultsOpenRef.current = !shouldClose;
    setResultsOpen(!shouldClose);
    setResultsState((current) => ({ ...current, selectedId: resultSetId }));
    if (!shouldClose) setResultsUpdated(false);
  }

  useEffect(() => {
    const syncStarterPromptCount = () => setUseCompactStarterPrompts(shouldUseCompactStarterPrompts());

    syncStarterPromptCount();
    window.addEventListener("resize", syncStarterPromptCount);
    window.addEventListener("orientationchange", syncStarterPromptCount);
    window.visualViewport?.addEventListener("resize", syncStarterPromptCount);

    return () => {
      window.removeEventListener("resize", syncStarterPromptCount);
      window.removeEventListener("orientationchange", syncStarterPromptCount);
      window.visualViewport?.removeEventListener("resize", syncStarterPromptCount);
    };
  }, []);

  useLayoutEffect(() => {
    resizeMessageInput();
  }, [input, messages.length]);

  useEffect(() => {
    const textarea = messageInputRef.current;
    if (!textarea) return;

    const resizeObserver = new ResizeObserver(() => resizeMessageInput(textarea));
    resizeObserver.observe(textarea);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia?.("(prefers-color-scheme: dark)");
    const syncTheme = () => setIsDarkMode(document.documentElement.classList.contains("dark") || !!mediaQuery?.matches);

    syncTheme();
    mediaQuery?.addEventListener?.("change", syncTheme);

    return () => mediaQuery?.removeEventListener?.("change", syncTheme);
  }, []);

  useEffect(() => {
    const chatId = currentChatId();
    const stateUrl = chatId ? `${appPath("/demo/state")}?chat_id=${encodeURIComponent(chatId)}` : appPath("/demo/state");

    fetch(stateUrl, { headers: { Accept: "application/json", ...releaseHeaders } })
      .then(async (response) => {
        if (await isStaleClientResponse(response)) {
          reloadWithRecoveredInput(input, {
            action: recoveredRequest?.action,
            notice: recoveredRequest?.notice,
            reloadAttempts: recoveryAttemptsRef.current,
          });
          return null;
        }
        if (!recoveredActionRef.current) recoveryAttemptsRef.current = 0;
        if (!response.ok) throw new Error("Could not load the existing demo chat.");
        return response.json();
      })
      .then((state: DemoState) => {
        if (!state) return;
        const restoredMessages = state.messages || [];
        setResultsState(restoreResults(restoredMessages, state.widgets));

        if (restoredMessages.some((message) => message.widgets?.length)) {
          setMessages(restoredMessages);
        } else if (state.widgets?.length && restoredMessages.length) {
          const next = [...restoredMessages];
          const lastAssistantIndex = next.findLastIndex((message) => message.role === "assistant");
          if (lastAssistantIndex >= 0) {
            next[lastAssistantIndex] = { ...next[lastAssistantIndex], widgets: state.widgets };
          }
          setMessages(next);
        } else {
          setMessages(restoredMessages);
        }
      })
      .catch(() => setError("Could not load the existing demo chat."))
      .finally(() => setInitialStateLoaded(true));
  }, []);

  useEffect(() => {
    if (!initialStateLoaded || !recoveredActionRef.current) return;

    const action = recoveredActionRef.current;
    recoveredActionRef.current = undefined;
    setRecoveryPending(false);
    if (action === "submit" && recoveredRequest?.input) void sendMessage(recoveredRequest.input, true);
    if (action === "reset") void resetChat(true);
  }, [initialStateLoaded]);

  useEffect(() => {
    if (messages.length === 0) return;

    if (pinSubmittedMessageRef.current && latestUserMessageRef.current) {
      pinSubmittedMessageRef.current = false;
      latestUserMessageRef.current.scrollIntoView({ block: "start" });
      shouldAutoScrollRef.current = false;
      return;
    }

    scrollToLatestMessage();
  }, [messages]);

  useEffect(() => {
    if (!resultsOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        resultsOpenRef.current = false;
        setResultsOpen(false);
        window.requestAnimationFrame(() => resultsLauncherRef.current?.focus());
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [resultsOpen]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === "demo-follow-up" && event.data.prompt) {
        setInput(event.data.prompt);
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  async function sendMessage(eventOrMessage?: FormEvent | string, recovering = false) {
    if (typeof eventOrMessage !== "string") eventOrMessage?.preventDefault();
    const rawMessage = (typeof eventOrMessage === "string" ? eventOrMessage : input).trim();
    if (!rawMessage || loading || (recoveryPending && !recovering)) return;
    const message = rawMessage;

    setError(null);
    setInput("");
    setLoading(true);
    setProcessingMessage("Thinking...");
    setResponseReadingSpace(Math.max((chatScrollRef.current?.clientHeight || 0) - 96, 0));
    pinSubmittedMessageRef.current = true;
    shouldAutoScrollRef.current = false;
    suppressResponseAutoScrollRef.current = true;
    setMessages((current) => [...current, { role: "user", content: message }, { role: "assistant", content: "" }]);

    let streamFinished = false;
    try {
      const response = await fetch(appPath("/demo/messages"), {
        method: "POST",
        headers: {
          Accept: "text/event-stream",
          "Content-Type": "application/json",
          ...releaseHeaders,
        },
        body: JSON.stringify({
          message,
          chat_id: currentChatId(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          ui_context: {
            results_presentation: window.matchMedia("(min-width: 64rem)").matches ? "side_panel" : "bottom_launcher",
          },
          model_selection: modelSelection,
          traveller_profile: readTravellerProfile(),
        }),
      });

      if (await isStaleClientResponse(response)) {
        reloadWithRecoveredInput(rawMessage, { action: "submit", reloadAttempts: recoveryAttemptsRef.current });
        return;
      }
      recoveryAttemptsRef.current = 0;
      if ([502, 503, 504].includes(response.status)) {
        reloadWithRecoveredInput(rawMessage, { notice: "The connection was interrupted. Your message is ready to send again." });
        return;
      }
      if (!response.ok || !response.body) {
        throw new Error("The demo chat request failed.");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";

        for (const part of parts) {
          const parsed = parseSSEEvent(part);
          if (!parsed) continue;

          if (parsed.event === "delta") {
            setMessages((current) => appendAssistantDelta(current, parsed.data.text || ""));
            scrollToLatestMessage();
          } else if (parsed.event === "widget") {
            setMessages((current) => appendAssistantWidget(current, parsed.data));
            setResultsState((current) => integrateWidget(current, parsed.data));
            if (!resultsOpenRef.current) setResultsUpdated(true);
          } else if (parsed.event === "tool_call") {
            setMessages((current) => appendAssistantToolCall(current, parsed.data));
            scrollToLatestMessage();
          } else if (parsed.event === "status") {
            setProcessingMessage(parsed.data.message || "Thinking...");
            scrollToLatestMessage();
          } else if (parsed.event === "done") {
            streamFinished = true;
            const completedMessages = parsed.data.messages || [];
            setMessages(completedMessages);
            setResultsState(restoreResults(completedMessages, parsed.data.widgets || []));
            scrollToLatestMessage();
          } else if (parsed.event === "error") {
            streamFinished = true;
            setError(parsed.data.message || "The demo chat failed.");
          }
        }
      }
      if (!streamFinished) throw new Error("The demo connection was interrupted.");
    } catch (err) {
      if (!streamFinished && (err instanceof TypeError || (err instanceof Error && err.message === "The demo connection was interrupted."))) {
        reloadWithRecoveredInput(rawMessage, { notice: "The connection was interrupted. Your message is ready to send again." });
      } else {
        setError(err instanceof Error ? err.message : "The demo chat failed.");
      }
    } finally {
      suppressResponseAutoScrollRef.current = false;
      setLoading(false);
    }
  }

  async function resetChat(recovering = false) {
    if (recoveryPending && !recovering) return;

    setError(null);
    try {
      const response = await fetch(appPath("/demo"), {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...releaseHeaders,
        },
        body: JSON.stringify({ chat_id: currentChatId() }),
      });
      if (await isStaleClientResponse(response)) {
        reloadWithRecoveredInput("", { action: "reset", reloadAttempts: recoveryAttemptsRef.current });
        return;
      }
      recoveryAttemptsRef.current = 0;
      if (!response.ok) throw new Error("Could not reset the demo chat.");
      const data = await response.json();
      setMessages([]);
      setResultsState({ sets: [], selectedId: null });
      setResultsOpen(false);
      setResultsUpdated(false);
      setResponseReadingSpace(0);
      window.location.assign(data.redirect_url || appPath("/demo"));
    } catch {
      setError("Could not reset the demo chat.");
    }
  }

  const starterPrompts = useCompactStarterPrompts ? STARTER_PROMPTS.slice(0, 3) : STARTER_PROMPTS;
  const activeResultSet = resultsState.sets.find((resultSet) => resultSet.id === resultsState.selectedId) || resultsState.sets.at(-1);
  const activeWidget = activeResultSet?.widget || null;
  const resultsCount = itineraryCount(activeWidget);
  const latestUserMessageIndex = messages.findLastIndex((message) => message.role === "user");
  const hasResults = resultsState.sets.length > 0;

  return (
    <main className="h-[100dvh] min-h-[100dvh] w-full overflow-hidden bg-white text-gray-900 dark:bg-black dark:text-gray-100">
      <div className="flex h-full min-w-0 flex-col overflow-hidden">
        <header className="z-20 flex-none border-b border-gray-100 bg-white/90 px-4 py-3 backdrop-blur dark:border-white/10 dark:bg-black/90">
          <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-3">
              <a href={appPath("/demo")} aria-label="Start a new demo search" className="shrink-0">
                <img src={appPath("/images/logo_with_word_blue.png")} alt="Reagent" className="h-6 w-auto sm:h-8" />
              </a>
              <h1 className="hidden min-w-0 truncate text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100 sm:block">Conversational Flight Search Demo</h1>
            </div>
            <ProfileMenu
              disabled={recoveryPending}
              menuButtonRef={demoMenuButtonRef}
              onAdjustProfile={() => setProfileOpen(true)}
              onImportProfile={() => setProfileImportOpen(true)}
              onReset={() => resetChat()}
            />
          </div>
        </header>

        <div className={`mx-auto grid min-h-0 w-full max-w-[1600px] flex-1 grid-cols-1 grid-rows-[minmax(0,1fr)_auto] transition-[grid-template-columns] duration-500 lg:grid-rows-[minmax(0,1fr)_auto] ${hasResults ? "lg:grid-cols-[minmax(360px,0.82fr)_minmax(0,1.18fr)]" : "lg:grid-cols-[minmax(0,1fr)_minmax(0,0fr)]"}`}>
          <section className={`relative col-start-1 row-start-1 flex min-h-0 min-w-0 flex-col overflow-hidden ${hasResults ? "lg:border-r lg:border-gray-200 lg:dark:border-white/10" : ""}`} aria-label="Conversation">
            <div
              ref={chatScrollRef}
              onScroll={() => { shouldAutoScrollRef.current = isNearBottom(); }}
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
            >
              <div className="mx-auto w-full max-w-[900px] px-0 py-6 sm:px-4">
                {initialStateLoaded && messages.length === 0 ? (
                  <div className="mx-auto max-w-xl px-4 pt-8 text-center sm:mt-[12vh] sm:px-0 sm:pt-0">
                    <h2 className={`text-2xl font-semibold tracking-tight sm:text-3xl ${isDarkMode ? "text-gray-100" : "text-gray-900"}`}>Find your best trip, not more options</h2>
                    <p className="mt-3 text-base text-gray-500 dark:text-gray-400">Just ask naturally, then keep going. Reagent remembers what matters, does the heavy lifting, and lets you refine for anything along the way.</p>
                    <div className="mt-8 grid gap-3 text-left sm:grid-cols-2">
                      {starterPrompts.map((example) => (
                        <button
                          key={example.label}
                          type="button"
                          onClick={() => sendMessage(example.prompt)}
                          disabled={loading || recoveryPending}
                          className="rounded-2xl border border-gray-200 p-4 text-sm text-gray-700 transition hover:bg-gray-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
                        >
                          {example.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                <div className="space-y-7">
                  {messages.map((message, index) => {
                    const suggestions = message.role === "assistant" ? suggestionTexts(message.content) : [];

                    return (
                      <div
                        key={`${message.role}-${index}`}
                        ref={message.role === "user" && index === latestUserMessageIndex ? latestUserMessageRef : undefined}
                        className={message.role === "user" ? "scroll-mt-6 flex justify-end px-3 sm:px-0" : "block"}
                        style={message.role === "assistant" && index === messages.length - 1 && responseReadingSpace ? { minHeight: responseReadingSpace } : undefined}
                      >
                        {message.role === "user" ? (
                          <div className="max-w-[80%] whitespace-pre-wrap rounded-3xl bg-[#f4f4f4] px-5 py-3 text-[15px] leading-6 text-gray-900 dark:bg-[#303030] dark:text-gray-100 sm:text-base sm:leading-7">
                            {message.content}
                          </div>
                        ) : (
                          <div className="min-w-0 w-full overflow-x-hidden text-[15px] leading-6 text-gray-900 dark:text-gray-100 sm:text-base sm:leading-7">
                            {debugEnabled && (!loading || index !== messages.length - 1) && (suggestions.length || (message.widgets?.length && (message.tool_calls?.length || message.usage))) ? (
                              <ToolCallsDebug toolCalls={message.tool_calls || []} usage={message.usage} conversationUsage={cumulativeUsage(messages, index)} suggestions={suggestions} />
                            ) : null}
                            <div className={message.tool_calls?.length ? "mt-px px-3 sm:px-0" : "px-3 sm:px-0"}>
                              {message.content ? (
                                <MarkdownMessage text={message.content} onSuggestion={sendMessage} suggestionsVisible={!loading || index !== messages.length - 1} />
                              ) : loading && index === messages.length - 1 ? (
                                processingMessage
                              ) : null}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>

            <div className={`col-start-1 row-start-2 border-t border-gray-100 bg-white/95 px-3 pb-[calc(env(safe-area-inset-bottom,0px)+0.75rem)] pt-2 backdrop-blur dark:border-white/10 dark:bg-black/95 sm:px-4 ${hasResults ? "lg:border-r lg:border-gray-200 lg:dark:border-white/10" : ""}`}>
              {resultsState.sets.length ? (
                <div className="mx-auto mb-2 grid w-full max-w-[900px] grid-cols-2 gap-2 lg:hidden">
                  {resultsState.sets.map((resultSet, index) => {
                    const count = itineraryCount(resultSet.widget);
                    const selected = resultSet.id === activeResultSet?.id;
                    const expanded = resultsOpen && selected;
                    const action = expanded
                      ? (count === 1 ? "Hide itinerary" : "Hide itineraries")
                      : (count === 1 ? "View itinerary" : `View all ${count} itineraries`);
                    const spansFullWidth = resultsState.sets.length % 2 === 1 && index === resultsState.sets.length - 1;

                    return (
                      <button
                        key={resultSet.id}
                        ref={selected ? resultsLauncherRef : undefined}
                        type="button"
                        onClick={() => toggleResultSet(resultSet.id)}
                        aria-expanded={expanded}
                        aria-controls="demo-results-panel"
                        className={`${spansFullWidth ? "col-span-2" : "col-span-1"} flex min-w-0 items-center justify-between gap-2 rounded-2xl border px-3 py-2.5 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${selected ? "border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 dark:border-blue-400/30 dark:bg-blue-400/10 dark:text-blue-200 dark:hover:bg-blue-400/20" : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 dark:hover:bg-white/10"}`}
                      >
                        <span className="min-w-0">
                          {resultsState.sets.length > 1 ? <span className="block truncate text-[11px] font-medium text-gray-500 dark:text-gray-400">{resultSet.label}</span> : null}
                          <span className="block truncate text-sm font-semibold">{action}</span>
                        </span>
                        <span className="flex shrink-0 items-center gap-2">
                          {resultsUpdated && selected ? <span className="h-2 w-2 rounded-full bg-blue-600 dark:bg-blue-300" aria-label="Results updated" /> : null}
                          <span aria-hidden="true">{expanded ? "↓" : "↑"}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : null}

              {error ? <div role="alert" className="mx-auto mb-2 max-w-[900px] rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm dark:border-red-900/50 dark:bg-red-950 dark:text-red-200">{error}</div> : null}

              <form onSubmit={sendMessage}>
                <div className={`mx-auto max-w-[900px] rounded-[26px] border border-gray-200 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.02),0_8px_28px_rgba(0,0,0,0.08)] dark:border-white/10 dark:bg-[#303030] dark:shadow-none ${messages.length > 0 ? "flex items-center gap-1 p-1.5" : "p-2"}`}>
                  <label className="sr-only" htmlFor="demo-message">Message</label>
                  <textarea
                    ref={messageInputRef}
                    id="demo-message"
                    value={input}
                    disabled={recoveryPending}
                    onFocus={() => {
                      modelSelectorRef.current?.removeAttribute("open");
                    }}
                    onChange={(event) => {
                      setInput(event.target.value);
                      resizeMessageInput(event.target);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        sendMessage();
                      }
                    }}
                    rows={1}
                    placeholder={messages.length > 0 ? "What should we refine..." : "Tell me about your trip..."}
                    className={`block w-full flex-1 resize-none bg-transparent px-3 text-[15px] text-gray-900 outline-none placeholder:text-gray-400 dark:text-gray-100 dark:placeholder:text-gray-500 sm:text-base ${messages.length > 0 ? "min-h-10 py-2" : "min-h-12 py-3"}`}
                  />
                  {messages.length > 0 ? (
                    <button
                      type="submit"
                      disabled={loading || recoveryPending || !input.trim()}
                      aria-label="Send message"
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#1264df] text-white transition hover:bg-[#0f55bf] disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 dark:bg-[#1264df] dark:text-white dark:hover:bg-[#2b78e8] dark:disabled:bg-white/15 dark:disabled:text-gray-500"
                    >
                      <span aria-hidden="true">↑</span>
                    </button>
                  ) : (
                    <div className="flex items-center justify-between gap-2 px-1 pb-1">
                      <div className="ml-auto flex min-w-0 items-center gap-2">
                        {!hideModelSelector && initialStateLoaded ? (
                          <ModelSelector selection={modelSelection} onChange={selectModel} detailsRef={modelSelectorRef} />
                        ) : null}
                        <button
                          type="submit"
                          disabled={loading || recoveryPending || !input.trim()}
                          aria-label="Send message"
                          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#1264df] text-white transition hover:bg-[#0f55bf] disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 dark:bg-[#1264df] dark:text-white dark:hover:bg-[#2b78e8] dark:disabled:bg-white/15 dark:disabled:text-gray-500"
                        >
                          <span aria-hidden="true">↑</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </div>

          {hasResults ? (
            <section
              id="demo-results-panel"
              aria-label="Flight results"
              className={`${resultsOpen ? "flex" : "hidden"} z-10 col-start-1 row-start-1 min-h-0 min-w-0 flex-col bg-[#f7f8fa] dark:bg-black lg:z-auto lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:flex`}
            >
              <div className="hidden flex-none border-b border-gray-200 bg-[#f7f8fa]/95 px-5 py-3 backdrop-blur dark:border-white/10 dark:bg-black/95 lg:block">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h2 id="demo-results-title" className="font-semibold text-gray-900 dark:text-gray-100">Your flight options</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{resultsCount} {resultsCount === 1 ? "option" : "options"} · Keep chatting to refine</p>
                  </div>
                  {resultsState.sets.length > 1 ? <span className="text-xs text-gray-400">{resultsState.sets.length} searches</span> : null}
                </div>
                {resultsState.sets.length > 1 ? (
                  <div role="tablist" aria-label="Flight searches" className="mt-3 flex flex-wrap gap-2">
                    {resultsState.sets.map((resultSet) => {
                      const selected = resultSet.id === activeResultSet?.id;
                      const count = itineraryCount(resultSet.widget);
                      return (
                        <button
                          key={resultSet.id}
                          type="button"
                          role="tab"
                          aria-selected={selected}
                          aria-controls="demo-results-content"
                          onClick={() => setResultsState((current) => ({ ...current, selectedId: resultSet.id }))}
                          className={`max-w-full rounded-full border px-3 py-1.5 text-left text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${selected ? "border-blue-600 bg-blue-600 text-white dark:border-blue-400 dark:bg-blue-400 dark:text-gray-950" : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50 dark:border-white/10 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10"}`}
                        >
                          <span className="block max-w-64 truncate">{resultSet.label}</span>
                          <span className={selected ? "text-blue-100 dark:text-gray-800" : "text-gray-400"}>{count} {count === 1 ? "itinerary" : "itineraries"}</span>
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
              <div id="demo-results-content" role="tabpanel" className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-0 pb-[env(safe-area-inset-bottom,0px)] sm:px-5 sm:py-3">
                {activeWidget ? (
                  <div className="mx-auto w-full max-w-[1100px] overflow-hidden border-y border-gray-200 bg-white dark:border-white/10 dark:bg-black sm:rounded-2xl sm:border">
                    <ResultsWidget key={activeResultSet?.id} widget={activeWidget} />
                  </div>
                ) : (
                  <div className="hidden h-full items-center justify-center px-8 text-center text-sm text-gray-400 dark:text-gray-500 lg:flex">
                    Flight options will appear here as they are found.
                  </div>
                )}
              </div>
            </section>
          ) : null}
        </div>
      </div>
      <ProfileDialog
        key={profileRevision}
        open={profileOpen}
        onClose={() => {
          setProfileOpen(false);
          window.requestAnimationFrame(() => demoMenuButtonRef.current?.focus());
        }}
      />
      <ImportProfileDialog
        open={profileImportOpen}
        onImported={() => setProfileRevision((revision) => revision + 1)}
        onClose={() => {
          setProfileImportOpen(false);
          window.requestAnimationFrame(() => demoMenuButtonRef.current?.focus());
        }}
      />
      <style>{`
        .demo-itinerary-widget :is(a, button)[data-pill] {
          border-radius: 9999px !important;
        }

        .demo-itinerary-widget :is(a, button)[data-variant] {
          min-height: 2.75rem !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-weight: 600 !important;
          line-height: 1.2 !important;
          text-decoration: none !important;
          -webkit-appearance: none !important;
          appearance: none !important;
        }

        .demo-itinerary-widget :is(a, button)[data-variant] > span {
          color: inherit !important;
        }

        .demo-itinerary-widget :is(a, button)[data-variant="solid"] {
          background: #f4f4f4 !important;
          color: #111 !important;
          border: 0 !important;
          padding-left: 1.35rem !important;
          padding-right: 1.35rem !important;
        }

        .demo-itinerary-widget :is(a, button)[data-variant="solid"][aria-disabled="true"],
        .demo-itinerary-widget :is(a, button)[data-variant="solid"][data-disabled] {
          background: #f4f4f4 !important;
          color: #111 !important;
          opacity: 1 !important;
        }

        .demo-itinerary-widget :is(a, button)[data-variant="soft"][data-color="secondary"] {
          background: #3b3b3b !important;
          color: #fff !important;
          border: 0 !important;
        }

        .demo-itinerary-widget :is(a, button):focus-visible {
          outline: 2px solid rgba(255, 255, 255, 0.45) !important;
          outline-offset: 2px !important;
        }

        html:not(.dark) .demo-itinerary-widget :is(a, button)[data-variant="soft"][data-color="secondary"] {
          background: #ececec !important;
          color: #111 !important;
        }

        .demo-itinerary-widget[data-demo-theme="light"] :is(a, button) {
          --button-background-color: #f4f4f4 !important;
          --button-background-color-hover: #e8e8e8 !important;
          --button-background-color-active: #dddddd !important;
          --button-text-color: #111 !important;
          --button-ring-color: rgba(17, 17, 17, 0.28) !important;
          background: #f4f4f4 !important;
          color: #111 !important;
          border-color: transparent !important;
          -webkit-text-fill-color: #111 !important;
        }

        .demo-itinerary-widget[data-demo-theme="light"] :is(a, button)::before {
          background-color: var(--button-background-color) !important;
          box-shadow: none !important;
        }

        .demo-itinerary-widget[data-demo-theme="light"] :is(a, button)[data-variant="soft"][data-color="secondary"] {
          --button-background-color: #ececec !important;
          --button-background-color-hover: #e0e0e0 !important;
          --button-background-color-active: #d6d6d6 !important;
          --button-text-color: #111 !important;
          background: #ececec !important;
          color: #111 !important;
          -webkit-text-fill-color: #111 !important;
        }

        .demo-itinerary-widget[data-demo-theme="light"] :is(a, button)[data-variant] > span {
          color: inherit !important;
          -webkit-text-fill-color: inherit !important;
        }

        @media (max-width: 767px), (hover: none) and (pointer: coarse) {
          .demo-itinerary-widget [class~="p-3"][class~="flex"][class~="gap-3"] {
            padding-left: 0.5rem !important;
            padding-right: 0.5rem !important;
            column-gap: 0.5rem !important;
          }

          .demo-itinerary-widget [class~="min-w-[90px]"] {
            min-width: 76px !important;
          }

          .demo-itinerary-widget [class~="px-4"][class~="py-3"] {
            padding-left: 0.5rem !important;
            padding-right: 0.5rem !important;
          }

          .demo-itinerary-widget [class~="rounded-xl"][class~="px-3"][class~="py-2"] {
            min-width: 0 !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }

          .demo-itinerary-widget [class~="inline-flex"][class~="justify-end"][class~="truncate"] {
            min-width: 0 !important;
            max-width: 100% !important;
            white-space: normal !important;
            overflow: visible !important;
            text-overflow: clip !important;
            flex-wrap: wrap !important;
          }

          .demo-itinerary-widget [class~="items-center"][class~="justify-between"][class~="text-sm"] > [class~="items-center"][class~="min-w-0"] {
            align-items: flex-start !important;
            flex-wrap: wrap !important;
            row-gap: 0.25rem !important;
          }

          .demo-itinerary-widget [class~="items-center"][class~="justify-between"][class~="text-sm"] [class~="min-w-0"][class~="flex-1"] {
            flex: 1 1 44% !important;
          }

          .demo-itinerary-widget [class~="text-[11px]"][class~="gap-3"] > div {
            min-width: 0 !important;
            white-space: normal !important;
          }

          .demo-itinerary-widget :is(a, button)[data-variant="soft"][data-color="secondary"] {
            min-height: 2.25rem !important;
            height: 2.25rem !important;
            padding-top: 0 !important;
            padding-bottom: 0 !important;
            padding-left: 1.5rem !important;
            padding-right: 1.5rem !important;
            min-width: 7rem !important;
          }

          .demo-itinerary-widget :is(a, button)[data-variant="solid"] {
            min-height: 2.25rem !important;
            height: 2.25rem !important;
            padding-top: 0 !important;
            padding-bottom: 0 !important;
          }

          .demo-itinerary-widget[data-demo-theme="light"] :is(a, button)[data-variant="solid"] {
            --button-background-color: #2f2f2f !important;
            --button-background-color-hover: #242424 !important;
            --button-background-color-active: #1a1a1a !important;
            --button-text-color: #fff !important;
            background: #2f2f2f !important;
            color: #fff !important;
            -webkit-text-fill-color: #fff !important;
          }

          .demo-itinerary-widget[data-demo-theme="light"] :is(a, button)[data-variant="solid"]::before {
            background-color: #2f2f2f !important;
          }
        }

        .demo-itinerary-widget :is(div, span)[data-variant][data-color][data-size] {
          padding-left: 0.55rem !important;
          padding-right: 0.55rem !important;
        }

        @media (max-width: 767px), (hover: none) and (pointer: coarse) {
          .demo-starter-extra {
            display: none !important;
          }
        }
      `}</style>
    </main>
  );
}

const demoRoot = document.getElementById("demo-root");

if (demoRoot) {
  const recoveredRequest = takeRecoveredRequest();
  const production = demoRoot.dataset.demoProduction === "true";

  createRoot(demoRoot).render(
    <React.StrictMode>
      <DemoApp
        demoRelease={demoRoot.dataset.demoRelease}
        debugEnabled={!production}
        hideModelSelector={production}
        recoveredRequest={recoveredRequest}
      />
    </React.StrictMode>
  );
}
